import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useState, useMemo, useCallback, useEffect } from "react";
import { getFilteredIdeas, getFilteredThemes, calculateAggregations } from "../api/ideas";
import { isAiView, getAiViewAccessors, AI_VIEW_DEFAULT_SORT } from "../utils/viewUtils";
import { saveIdeaStarred, getViews, addViews } from "../Api";
import { extractAggregationColumns, updateAccessorByState } from "../utils";
import { getNestedValue } from "../components/ideas/ideas-table-helpers/Helpers";
import { ActiveFilter } from "../components/ideas/filters/FiltersMenu";
import { showNotification } from "@mantine/notifications";
import { useIdeasUrlState } from "./useIdeasUrlState";
import { DataTableSortStatus } from "mantine-datatable";
import { EvidenceInclusion } from "../components/ideas/store/types";
import { useIdeaPages } from "./useIdeaPages";
import {
  getAvailableColumnSources,
  type AvailableColumnSourcesResponse
} from "../api/idea";
import { ideaKeys } from "./ideaPage/ideaKeys";
import { useCurrentUser } from "./useCurrentUser";

const PAGE_SIZE = 50;

interface UseIdeasParams {
  search?: string;
  activeFilters?: ActiveFilter[];
  isArchived?: boolean;
  subPageKey?: string;
  pathname?: string;
  evidenceInclusion?: EvidenceInclusion;
  selectedView?: any; // Merged selectedView object from component (for view filters)
  isInitializing?: boolean; // Prevent query from running during initialization
  // True from the render where subPageKey changes until the merge effect has
  // recomputed activeFilters for the new subpage.
  selectedViewIsStale?: boolean;
  isExplicitSort?: boolean; // Whether the user has explicitly clicked a column to sort during active search
}

interface UseIdeasReturn {
  ideas: any[];
  loading: boolean;
  aggregationsLoading: boolean;
  error: Error | null;
  totalCount: number;
  refetch: () => void;
  starIdea: (ideaId: string, isStarred: boolean, idea: any) => Promise<void>;
  // Views management
  views: any[];
  viewsLoading: boolean;
  upsertView: (viewData: any, callback?: (res: any) => void) => Promise<any>;
  // Columns management
  columns: any[];
  columnsLoading: boolean;
  // Cache management
  clearCache: () => void;
  invalidateFilters: () => void;
  invalidateSubPageCache: () => Promise<void>;
  getCacheKey: () => readonly any[];
  subPage: any;
  subPageLoading: boolean;
  // URL state management
  page: string;
  sortStatus: DataTableSortStatus | null;
  effectiveSortStatus: DataTableSortStatus | null;
  selectedViewName: string | null;
  globalFilters: {
    owner?: string[];
    productArea?: string[];
    dateRange?: { start: string; end: string } | null;
  };
  tabFilters: any[];
  setPage: (page: string) => void;
  setSortStatus: (sortStatus: DataTableSortStatus | null | ((prev: DataTableSortStatus | null) => DataTableSortStatus | null)) => void;
  setSelectedViewName: (name: string | null) => void;
  setGlobalFilters: (filters: { owner?: string[]; productArea?: string[]; dateRange?: { start: string; end: string } | null }) => void;
  setTabFilters: (filters: any[]) => void;
}

// Query keys factory - more granular for better caching (exported for targeted refetch)
export const ideasKeys = {
  all: ["ideas"] as const,
  lists: () => [...ideasKeys.all, "list"] as const,
  list: (params: {
    search: string;
    activeFilters: ActiveFilter[];
    sort: any;
    page: string;
    isArchived: boolean;
    pathname: string;
    subPageKey?: string;
    viewName?: string;
  }) => [...ideasKeys.lists(), "filtered", params] as const,
  // Helper to invalidate all lists
  invalidateAll: () => [...ideasKeys.lists()] as const,
  views: (subPageKey?: string) => [...ideasKeys.all, "views", subPageKey || "main"] as const,
};

export const useIdeas = (params: UseIdeasParams): UseIdeasReturn => {
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  
  const {
    search = "",
    activeFilters = [],
    isArchived = false,
    selectedView,
    subPageKey,
    pathname = "",
    evidenceInclusion = "total",
    isInitializing = false,
    selectedViewIsStale = false,
    isExplicitSort = false,
  } = params;

  const {
    page,
    sortStatus,
    selectedView: selectedViewName,
    globalFilters,
    tabFilters,
    setPage,
    setSortStatus,
    setSelectedView: setSelectedViewName,
    setGlobalFilters,
    setTabFilters,
  } = useIdeasUrlState({ subPageKey });

  // Normalize subPageKey to ensure consistent caching behavior
  // For the main "all" ideas page, we want a consistent cache key regardless of route
  const normalizedSubPageKey = subPageKey === "all" ? "" : (subPageKey || "");

  // Lift idea-pages/columns lookup above the queryKey so we can validate the
  // active sort against the Bagel AI tab's actual columns when the AI view is
  // selected. Without this, a sort applied on the regular Ideas tab would
  // leak into the AI tab even when its column doesn't exist there.
  const { getPageByKey, isLoading: pagesLoading, invalidatePages } = useIdeaPages();

  const currentPage = useMemo(() => {
    const pageKey = normalizedSubPageKey || "all";
    return getPageByKey(pageKey);
  }, [normalizedSubPageKey, getPageByKey]);

  const columns = useMemo(() => {
    return currentPage?.ideaColumns || [];
  }, [currentPage]);

  // When the AI view is active, only honor the URL `sortStatus` if its
  // accessor is one of the AI tab's actual columns; otherwise fall back to
  // the AI tab default sort. The URL `sortStatus` is intentionally left
  // untouched so that switching back to the Ideas tab restores the original
  // sort.
  const effectiveSortStatus = useMemo(() => {
    if (!isAiView(selectedView)) return sortStatus;
    if (!columns.length) return sortStatus;
    const aiAccessors = getAiViewAccessors(columns);
    if (
      sortStatus?.columnAccessor &&
      aiAccessors.includes(sortStatus.columnAccessor)
    ) {
      return sortStatus;
    }
    return AI_VIEW_DEFAULT_SORT;
  }, [sortStatus, selectedView, columns]);

  // Extract stable values from selectedView to prevent unnecessary query key recalculations.
  // When URL has selectedView=All, use "All" for cache/API (default page view, not a saved view).
  const selectedViewStable = useMemo(() => {
    const urlIsAll = selectedViewName && selectedViewName.toLowerCase() === "all";
    const name = urlIsAll ? "All" : (selectedView?.name || selectedViewName || "");
    return {
      id: urlIsAll ? null : (selectedView?._id || selectedView?.id || null),
      name,
      isGlobal: selectedView?.isGlobal || false,
      isSubPageView: selectedView?.isSubPageView || false,
      filterCount: selectedView?.filters?.length || 0,
      operator: selectedView?.operator || 'AND',
      level: selectedView?.level || 'ideas',
    };
  }, [
    selectedView?._id,
    selectedView?.id,
    selectedView?.name,
    selectedView?.isGlobal,
    selectedView?.isSubPageView,
    selectedView?.filters?.length,
    selectedView?.operator,
    selectedView?.level,
    selectedViewName
  ]);

  // Check if view name is synchronized between selectedView and selectedViewName
  // This prevents the query from running with mismatched view names during view changes
  const isViewNameSynchronized = useMemo(() => {
    const viewNameFromProps = selectedView?.name || "";
    const viewNameFromUrl = selectedViewName || "";
    
    // If both are empty, they're synchronized
    if (!viewNameFromProps && !viewNameFromUrl) {
      return true;
    }
    
    // "All" is the default label for /ideas/all route - it is not a saved view from API.
    // When URL has selectedView=All, allow the query to run immediately (no view object needed).
    if (viewNameFromUrl && viewNameFromUrl.toLowerCase() === "all") {
      return true;
    }
    
    // If selectedViewName is set (from URL), wait until selectedView matches it
    // This prevents running the query with the old view name during view changes
    if (viewNameFromUrl) {
      // If selectedView is not set yet, wait for it
      if (!viewNameFromProps) {
        return false;
      }
      // Check if they match (case-insensitive)
      return viewNameFromProps.toLowerCase() === viewNameFromUrl.toLowerCase();
    }
    
    // If selectedViewName is not set, allow the query to run (no view selected)
    return true;
  }, [selectedView?.name, selectedViewName]);

  const queryKey = useMemo(() => {
    // Create sorting object for API and cache key.
    // During an active search, only include sort when the user has explicitly clicked a column header.
    // Raw searches (no explicit sort) are sorted by search score on the backend.
    const sort: any = {};
    const isRawSearch = !!(search?.trim()) && !isExplicitSort;
    if (!isRawSearch && effectiveSortStatus?.columnAccessor) {
      const transformedAccessor = updateAccessorByState(effectiveSortStatus.columnAccessor, evidenceInclusion);
      sort[transformedAccessor] = effectiveSortStatus.direction === "asc" ? 1 : -1;
    }
    
    // Normalize page to string
    const pageStr = page || "1";

    // Create a comprehensive, stable representation of filters for caching
    const stableFilters = activeFilters?.map(filter => ({
      parent: filter.parent,
      selected: Array.isArray(filter.selected) 
        ? [...filter.selected].sort() 
        : filter.selected,
      operator: filter.operator,
      type: filter.type,
      name: filter.name,
      query: filter.query
    })).sort((a, b) => {
      // Handle cases where parent might be undefined
      const parentA = a.parent || '';
      const parentB = b.parent || '';
      return parentA.localeCompare(parentB);
    }) || [];

    // Enhanced cache parameters that maintain compatibility but add granularity
    
    const cacheParams = {
      search: search?.trim() || "",
      activeFilters: stableFilters,
      sort,
      page: pageStr,
      isArchived: !!isArchived,
      pathname: pathname || "",
      subPageKey: normalizedSubPageKey,
      viewName: selectedViewStable.name,
      viewOperator: selectedViewStable.operator,
      viewLevel: selectedViewStable.level,
      evidenceInclusion: evidenceInclusion || "total",
      
      // Additional granular cache properties for better separation
      _meta: {
        // Filter metadata for granular caching
        filterCount: stableFilters.length,
        filterSignature: stableFilters.map(f => 
          `${f.parent}:${f.type}:${f.operator || 'none'}:${Array.isArray(f.selected) ? f.selected.join(',') : f.selected || 'none'}`
        ).join('|'),
        
        // Sorting metadata
        sortColumn: effectiveSortStatus?.columnAccessor || 'none',
        sortDirection: effectiveSortStatus?.direction || 'none',
        sortActive: !!(effectiveSortStatus?.columnAccessor && effectiveSortStatus?.direction),
        isExplicitSort,
        
        // Pagination metadata
        pageNumber: parseInt(pageStr, 10),
        isFirstPage: (parseInt(pageStr, 10)) === 1,
        
        // View metadata - use stable extracted values
        viewId: selectedViewStable.id,
        viewIsGlobal: selectedViewStable.isGlobal,
        viewIsSubPageView: selectedViewStable.isSubPageView,
        viewFilterCount: selectedViewStable.filterCount,
        
        // Context metadata
        isArchived: !!isArchived,
        hasSubPageKey: !!(normalizedSubPageKey && normalizedSubPageKey.trim()),
        
        // Cache time slot (rounded to 5 minutes for reasonable cache duration)
        cacheTimeSlot: Math.floor(Date.now() / (5 * 60 * 1000))
      }
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('🔑 Granular cache key generated:', {
        filtersCount: cacheParams._meta.filterCount,
        sortColumn: cacheParams._meta.sortColumn,
        sortDirection: cacheParams._meta.sortDirection,
        page: cacheParams._meta.pageNumber,
        view: cacheParams.viewName || 'none',
        filterSig: cacheParams._meta.filterSignature.substring(0, 50) + (cacheParams._meta.filterSignature.length > 50 ? '...' : ''),
        subPageKey: normalizedSubPageKey || 'main',
        cacheTimeSlot: cacheParams._meta.cacheTimeSlot
      });
    }

    return ideasKeys.list(cacheParams);
  }, [search, activeFilters, effectiveSortStatus, page, isArchived, pathname, normalizedSubPageKey, selectedViewStable, evidenceInclusion, isExplicitSort]);

  // Main ideas query
  const {
    data: ideasData,
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('🔄 Fetching ideas with params:', {
        search,
        activeFilters: activeFilters?.length || 0,
        sortStatus: effectiveSortStatus,
        page,
        isArchived,
        viewName: selectedView?.name
      });

      // Build sort for the API call.
      // During an active search, only include sort when the user has explicitly clicked a column header.
      // Raw searches (no explicit sort) are sorted by search score on the backend.
      const sort: any = {};
      const isRawSearch = !!(search?.trim()) && !isExplicitSort;
      if (!isRawSearch && effectiveSortStatus?.columnAccessor) {
        const transformedAccessor = updateAccessorByState(effectiveSortStatus.columnAccessor, evidenceInclusion);
        sort[transformedAccessor] = effectiveSortStatus.direction === "asc" ? 1 : -1;
      }

      const sanitizeFilter = (filter: Record<string, any>): Record<string, any> => {
        const { options, operations, childFilters, ...sanitizedFilter } = filter;
        
        if (childFilters && Object.keys(childFilters).length > 0) {
          sanitizedFilter.childFilters = Object.keys(childFilters).reduce(
            (acc: Record<string, any>, key: string) => {
              acc[key] = sanitizeFilter(childFilters[key]);
              return acc;
            },
            {}
          );
        }
        
        return sanitizedFilter;
      };

      // Process active filters (similar to current logic)
      const processedFilters = activeFilters?.map((filter) => sanitizeFilter(filter));

      const pageStr = page || "1";
      const skip = ((Number(pageStr) || 1) - 1) * PAGE_SIZE;
      const filtersPayload = {
        search,
        activeFilters: processedFilters,
        operator: selectedViewStable?.operator || 'AND',
      };

      // Theme-level fetching for Bagel AI tab
      if (isAiView(selectedView)) {
        const themeResponse = await getFilteredThemes(
          filtersPayload,
          sort,
          PAGE_SIZE,
          skip,
          auth0,
          evidenceInclusion
        );

        // Map theme rows to pseudo-idea rows so the table can render them uniformly
        const themeIdeas = (themeResponse.themes || []).map((theme: any) => ({
          _id: theme.themeKey,
          title: theme.themeTitle,
          description: theme.themeDescription,
          parentIdeaId: theme.parentIdeaId,
          parentIdeaTitle: theme.parentIdeaTitle,
          // Product Area on the AI tab can be picked by either accessor
          // (productItem.name or componentObj.name) depending on tenant config,
          // so we populate both with a minimal stub carrying only `name`.
          // These stubs lack link/itemKey/origin/status, so any cell that
          // consumes more than `.name` MUST gate on `_isThemeRow` (see
          // DeliveryStatusCell and StatusCell) or it will render broken UI.
          productItem: { name: theme.productArea, _isThemeStub: true },
          componentObj: { name: theme.productArea, _isThemeStub: true },
          evidenceIds: theme.evidenceIds,
          themeIndex: theme.themeIndex,
          state: theme.state || 'generated',
          _isThemeRow: true,
        }));

        return { ideas: themeIdeas, totalCount: themeResponse.totalCount || 0 };
      }

      // Regular ideas fetching
      // When URL has selectedView=All, pass "All" to API (it's the default page view, not a saved view)
      const viewNameForApi = (selectedViewName && selectedViewName.toLowerCase() === "all")
        ? "All"
        : (selectedView?.name || selectedViewName || "");
      const response = await getFilteredIdeas(
        {
          search,
          activeFilters: processedFilters,
          operator: selectedViewStable.operator,
        },
        sort,
        PAGE_SIZE,
        skip,
        viewNameForApi,
        auth0,
        isArchived,
        evidenceInclusion
      );

      console.log('✅ Ideas fetched:', response?.ideas?.length || 0, 'total:', response?.totalCount || 0);
      return response;
    },
    // For selectedView=All, run query immediately (no view filters to wait for). Otherwise wait for init and sync.
    enabled: !!auth0.isAuthenticated && !selectedViewIsStale && (isViewNameSynchronized && (selectedViewName?.toLowerCase() === "all" || !isInitializing)),
    // Bagel AI rows are synthesised per request from live parent-idea fields
    // (e.g. productItem.name). The caller flows that edit those fields don't
    // always invalidate ["ideas"], so we opt the AI view out of caching so that
    // returning to the tab always shows the latest parent data.
    staleTime: isAiView(selectedView) ? 0 : 30000,
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: isAiView(selectedView) ? 'always' : false,
  });

  // Views query
  const {
    data: viewsData,
    isLoading: viewsLoading,
    refetch: refetchViews
  } = useQuery({
    queryKey: ideasKeys.views(normalizedSubPageKey),
    queryFn: async () => {
      const response = await getViews(auth0);
      return response.views;
    },
    enabled: !!auth0.isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Star idea mutation
  const starMutation = useMutation({
    mutationFn: async ({ ideaId, isStarred, idea }: { ideaId: string; isStarred: boolean; idea: any }) => {
      return await saveIdeaStarred(idea, auth0);
    },
    onMutate: async ({ ideaId, isStarred }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);

      // Optimistically update the cache
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        
        const userId = currentUser?.id;
        const updatedIdeas = old.ideas.map((item: any) => {
          if (item._id === ideaId) {
            return {
              ...item,
              starred: isStarred ? userId : false,
            };
          }
          return item;
        });

        return {
          ...old,
          ideas: updatedIdeas,
        };
      });

      return { previousData };
    },
    onError: (err, variables, context) => {
      // Revert the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Upsert view mutation
  const upsertViewMutation = useMutation({
    mutationFn: async (viewData: any) => {
      return await addViews(viewData, auth0);
    },
    onSuccess: () => {
      // Invalidate and refetch views for the current subpage
      queryClient.invalidateQueries({ queryKey: ideasKeys.views(normalizedSubPageKey) });
      // Also invalidate views for other subpages to ensure consistency
      queryClient.invalidateQueries({ queryKey: [...ideasKeys.all, "views"] });
      showNotification({
        title: "View saved successfully!",
        color: "teal",
        message: "view name saved successfully",
        autoClose: 1200,
      });
    },
    onError: () => {
      showNotification({
        title: "Error!",
        color: "red",
        message: "Failed to save view",
        autoClose: 1200,
      });
    }
  });

  // State for aggregations
  const [aggregationsData, setAggregationsData] = useState<{ [ideaId: string]: any }>({});
  const [aggregationsLoading, setAggregationsLoading] = useState(false);

  // Clear aggregations data when filters change to show fresh skeletons
  useEffect(() => {
    setAggregationsData({});
    setAggregationsLoading(false);
  }, [search, activeFilters, effectiveSortStatus, page, isArchived, selectedView, selectedViewName, evidenceInclusion]);

  // Merge ideas with live aggregation data (pure data join, no sort)
  const mergedIdeas = useMemo(() => {
    const baseIdeas = ideasData?.ideas || [];

    if (Object.keys(aggregationsData).length === 0) {
      return baseIdeas;
    }

    return baseIdeas.map((idea: any) => {
      const aggregations = aggregationsData[idea._id];
      if (aggregations) {
        return {
          ...idea,
          aggregations: {
            ...idea.aggregations,
            ...aggregations
          }
        };
      }
      return idea;
    });
  }, [ideasData?.ideas, aggregationsData]);

  // Client-side re-sort for aggregation columns whose freshly computed
  // values may differ from what the backend used for ordering.
  const ideas = useMemo(() => {
    if (!mergedIdeas) return [];
    const isRawSearch = !!(search?.trim()) && !isExplicitSort;
    if (isRawSearch || !effectiveSortStatus?.columnAccessor?.startsWith('aggregations.')) {
      return mergedIdeas;
    }
    const accessor = updateAccessorByState(effectiveSortStatus.columnAccessor, evidenceInclusion);
    const direction = effectiveSortStatus.direction === 'asc' ? 1 : -1;
    return [...mergedIdeas].sort((a: any, b: any) => {
      const valA = getNestedValue(a, accessor) ?? 0;
      const valB = getNestedValue(b, accessor) ?? 0;
      if (valA < valB) return -1 * direction;
      if (valA > valB) return 1 * direction;
      return 0;
    });
  }, [mergedIdeas, effectiveSortStatus, evidenceInclusion, search, isExplicitSort]);

  const totalCount = useMemo(() => ideasData?.totalCount || 0, [ideasData?.totalCount]);
  const views = useMemo(() => viewsData || [], [viewsData]);
  const subPage = currentPage;

  // Cache management callbacks
  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: ideasKeys.all });
    console.log('🗑️ Ideas cache cleared');
  }, [queryClient]);

  const invalidateFilters = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ideasKeys.invalidateAll() });
    console.log('🔄 Ideas cache invalidated for filter changes');
  }, [queryClient]);

  const invalidateSubPageCache = useCallback(async () => {
    await Promise.all([
      invalidatePages(),
      queryClient.invalidateQueries({ queryKey: ideasKeys.views(normalizedSubPageKey) }),
      queryClient.invalidateQueries({ queryKey: ideasKeys.invalidateAll() }),
    ]);
    console.log('🔄 SubPage cache invalidated for normalizedSubPageKey:', normalizedSubPageKey || 'main');
  }, [queryClient, normalizedSubPageKey, invalidatePages]);

  const getCacheKey = useCallback(() => {
    return queryKey;
  }, [queryKey]);

  // Calculate aggregations when ideas and columns are fetched
  // Memoize column structure (excluding visibility) to prevent refetch on visibility changes
  const columnStructureKey = useMemo(() => {
    // Create a stable key based on column structure (excluding visibility state)
    return columns.map((col: any) => `${col.accessor}-${col.enabled}-${col.renderComponent || ''}`).sort().join('|');
  }, [columns]);

  useEffect(() => {
    const triggerAggregationCalculation = async () => {
      // Use base ideas data, not the merged ideas to avoid infinite loop
      const baseIdeas = ideasData?.ideas || [];
      
      // Only proceed if we have ideas, columns, and user is authenticated
      if (!baseIdeas.length || !columns.length || !auth0.isAuthenticated || loading) {
        setAggregationsLoading(false);
        return;
      }

      // For Bagel AI tab, always compute aggregations (themes carry their own evidence IDs).
      // For regular tabs, only compute when evidence filters are active.
      const hasEvidenceFilter = activeFilters?.some(filter => (filter as any).filterObject === 'evidence');
      if (!hasEvidenceFilter && !isAiView(selectedView)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('⏭️ No evidence filters found, skipping calculate-aggregations call');
        }
        setAggregationsLoading(false);
        return;
      }

      try {
        // Filter to only currently visible/enabled columns (same logic as IdeasTable)
        const visibleColumns = columns.filter((col: any) => col.enabled && !col.hidden);
        
        // Extract aggregation column names from only the visible columns
        const aggregationColumns = extractAggregationColumns(visibleColumns);
        
        // If no aggregation columns found, skip the call
        if (!aggregationColumns.length) {
          if (process.env.NODE_ENV === 'development') {
            console.log('⏭️ No aggregation columns found in visible columns, skipping calculate-aggregations call');
          }
          setAggregationsLoading(false);
          return;
        }

        // Process active filters (same logic as in the main query)
        const sanitizeFilter = (filter: Record<string, any>): Record<string, any> => {
          const { options, operations, childFilters, ...sanitizedFilter } = filter;
          
          if (childFilters && Object.keys(childFilters).length > 0) {
            sanitizedFilter.childFilters = Object.keys(childFilters).reduce(
              (acc: Record<string, any>, key: string) => {
                acc[key] = sanitizeFilter(childFilters[key]);
                return acc;
              },
              {}
            );
          }
          
          return sanitizedFilter;
        };

        const processedFilters = activeFilters?.map((filter) => sanitizeFilter(filter));
        
        // Prepare filters object in the same format as filters_query
        const filters = {
          search,
          activeFilters: processedFilters,
          operator: selectedViewStable.operator,
        };

        let result;

        // Theme-level aggregation path for Bagel AI tab
        if (isAiView(selectedView)) {
          const themeItems = baseIdeas
            .filter((idea: any) => idea._isThemeRow && idea.evidenceIds?.length > 0)
            .map((idea: any) => ({
              themeKey: idea._id,
              evidenceIds: idea.evidenceIds,
            }));

          if (!themeItems.length) {
            setAggregationsLoading(false);
            return;
          }

          setAggregationsLoading(true);

          if (process.env.NODE_ENV === 'development') {
            console.log('🧮 Triggering theme-level calculate-aggregations:', {
              aggregationColumns,
              themeItemsCount: themeItems.length,
            });
          }

          result = await calculateAggregations(aggregationColumns, undefined, filters, auth0, evidenceInclusion, themeItems);
        } else {
          // Standard idea-level aggregation path
          const ideaIds = baseIdeas.map((idea: any) => idea._id).filter(Boolean);
          
          if (!ideaIds.length) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⏭️ No idea IDs found, skipping calculate-aggregations call');
            }
            setAggregationsLoading(false);
            return;
          }

          setAggregationsLoading(true);

          if (process.env.NODE_ENV === 'development') {
            console.log('🧮 Triggering calculate-aggregations with:', {
              totalColumns: columns.length,
              visibleColumns: visibleColumns.length,
              aggregationColumns,
              ideaIdsCount: ideaIds.length,
              filtersCount: processedFilters?.length || 0,
              searchTerm: search || 'none'
            });
          }

          result = await calculateAggregations(aggregationColumns, ideaIds, filters, auth0, evidenceInclusion);
        }
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Aggregations calculated successfully:', result);
        }

        // Process the aggregations response and store it
        if (result && result.aggregations && Array.isArray(result.aggregations)) {
          const aggregationsMap: { [ideaId: string]: any } = {};
          
          result.aggregations.forEach((item: any) => {
            if (item.ideaId && item.aggregations) {
              aggregationsMap[item.ideaId] = item.aggregations;
            }
          });
          
          setAggregationsData(aggregationsMap);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 Aggregations data processed:', {
              totalAggregations: result.aggregations.length,
              ideaIds: Object.keys(aggregationsMap)
            });
          }
        }
        
        setAggregationsLoading(false);
      } catch (error) {
        console.error('❌ Error calculating aggregations:', error);
        setAggregationsLoading(false);
        // Don't throw the error to avoid disrupting the main ideas flow
      }
    };

    triggerAggregationCalculation();
  }, [ideasData?.ideas, columnStructureKey, auth0.isAuthenticated, loading, auth0, activeFilters, search, evidenceInclusion, selectedViewStable.operator]);

  // Callbacks
  const starIdea = useCallback(
    async (ideaId: string, isStarred: boolean, idea: any) => {
      await starMutation.mutateAsync({ ideaId, isStarred, idea });
    },
    [starMutation]
  );

  const upsertView = useCallback(
    async (viewData: any, callback?: (res: any) => void) => {
      const result = await upsertViewMutation.mutateAsync(viewData);
      callback?.(result);
      return result;
    },
    [upsertViewMutation]
  );

  return {
    ideas,
    loading,
    aggregationsLoading,
    error: error as Error | null,
    totalCount,
    refetch,
    starIdea,
    views,
    viewsLoading,
    upsertView,
    columns,
    columnsLoading: pagesLoading,
    // Cache management
    clearCache,
    subPage,
    subPageLoading: pagesLoading,
    invalidateFilters,
    invalidateSubPageCache,
    getCacheKey,
    // URL state management
    page,
    sortStatus,
    effectiveSortStatus,
    selectedViewName,
    globalFilters,
    tabFilters,
    setPage,
    setSortStatus,
    setSelectedViewName,
    setGlobalFilters,
    setTabFilters,
  };
};

/** Fetches column source options for the ideas table column manager (per page key). */
export const useAvailableColumnSources = (pageKey: string) => {
  const auth0 = useAuth0();

  return useQuery<AvailableColumnSourcesResponse>({
    queryKey: ideaKeys.availableColumnSources(pageKey),
    queryFn: () => getAvailableColumnSources(pageKey, auth0),
    enabled: !!auth0.isAuthenticated && pageKey.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
